/**
 * Everything Ownly has already built and still runs, one entry per capability.
 *
 * Generated from the capability catalogue in command-center, which records the
 * source file behind every line. Two exclusions are deliberate and permanent:
 * the trade-supplies client is confidential, and the demo templates have no
 * client behind them, so neither counts as evidence.
 *
 * Do not hand-edit. Regenerate from the catalogue instead.
 */

export type BuiltGroupSlug =
  | "booking-and-payments"
  | "portals-and-paperwork"
  | "sales-and-follow-up"
  | "apps-and-members"
  | "sites-and-content"
  | "running-the-business";

export type BuiltItem = {
  /** Stable id, used for the anchor on the catalogue page. */
  id: string;
  title: string;
  /** What the thing does, in a sentence. */
  what: string;
  /** What it means for the person paying for it. */
  outcome: string;
  /** The live product it runs in. */
  product: string;
  flagship: boolean;
};

export type BuiltGroup = {
  slug: BuiltGroupSlug;
  name: string;
  blurb: string;
  items: readonly BuiltItem[];
};

export const BUILT_GROUPS: readonly BuiltGroup[] = [
  {
    "slug": "booking-and-payments",
    "name": "Booking, tickets and getting paid",
    "blurb": "Taking money and filling a diary, without a phone call in the middle of it.",
    "items": [
      {
        "id": "instant-quote-generator",
        "title": "Instant quote generator",
        "what": "An interactive tool on the client's own website where a visitor picks their project type, page count, features, platforms and support level and instantly gets a priced range in pounds, calculated live from a configurable pricing model.",
        "outcome": "Prospects get a real budget figure while they are still interested, so they self qualify and the owner only spends time on people who can afford the work.",
        "product": "Ownly Digital",
        "flagship": true
      },
      {
        "id": "scope-to-payment",
        "title": "Scope to paid deposit in one screen",
        "what": "One flow where the team scopes a project, agrees the number, then generates a Stripe payment link supporting a deposit plus balance or split instalments, without leaving the page.",
        "outcome": "It turns a verbal yes into a paid deposit link on the spot, before the client cools off.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "in-app-branded-booking",
        "title": "Booking inside your own branded app",
        "what": "A booking screen that embeds the right coach's calendar directly inside the app, themed to the brand, so clients book and pay without leaving to a plain external page.",
        "outcome": "Clients book their next session in a couple of taps inside your own app, so the calendar fills without sending people elsewhere.",
        "product": "ProFlow Academy",
        "flagship": false
      },
      {
        "id": "prepaid-session-tokens",
        "title": "Prepaid session balance tracker",
        "what": "A prepaid-session system where staff grant a client a block of sessions, log a completed session to reduce the balance with an undo, and see remaining count and expiry per block.",
        "outcome": "You sell sessions in packages and the app tracks every client's balance automatically, so nobody over books and you always know who needs to top up.",
        "product": "ProFlow Academy",
        "flagship": false
      },
      {
        "id": "multi-step-booking-sync",
        "title": "Multi-step booking with email and planner sync",
        "what": "A multi-step booking enquiry that validates the input, saves the request, emails the team and the enquirer, pushes the lead into the booking system, and respects messaging consent for data rules.",
        "outcome": "Every enquiry is captured, confirmed by email, and dropped straight into the booking system, so no lead slips through the cracks.",
        "product": "The DFC Experience",
        "flagship": false
      },
      {
        "id": "gs-host-free-event",
        "title": "Filling an event without an audience to invite",
        "what": "Any signed-in member can host an event, and every event is discoverable by what it is about rather than by who the host is: browsable by category (Networking, Workshop, Mastermind, Pitch Event, Roundtable, Seminar, Training and eleven more), by city with a distance radius, and by industry keyword. There is no follower graph, so reach does not depend on audience size.",
        "outcome": "People who care about your subject find your event by searching for that subject, so a host with no following fills a room on the strength of the idea instead of the audience.",
        "product": "GrowthSync",
        "flagship": true
      },
      {
        "id": "gs-ticket-payouts",
        "title": "Sell tickets and get paid, on any plan",
        "what": "A host connects a Stripe account, sells paid tickets in tiers, and receives the money. While event hosting is open, any member can connect an account, not just paid tiers. Stripe still has to approve the account before a paid event can publish.",
        "outcome": "Your event pays for itself instead of costing you, so putting people together stops being a favour and starts being a business line.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "gs-door-scan",
        "title": "Scan tickets on the door, and hand the scanner to someone else",
        "what": "The host checks attendees in by scanning their ticket on a phone at the door, against a live attendee list. The host can also invite someone else to scan by sending them a link, and that link stops working at midnight on the event date.",
        "outcome": "Nobody queues at your door, and you are not stuck on it. You are inside meeting the people you invited.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "gs-ticket-wallet",
        "title": "The ticket lives in Apple or Google Wallet",
        "what": "A booked ticket is added to Apple Wallet or Google Wallet as a pass, and the pass updates itself if the event details change.",
        "outcome": "More of the people who said yes actually turn up, because the ticket is already in their phone.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "gs-ticket-transfer",
        "title": "Pass your ticket to someone else",
        "what": "An attendee who can no longer make it transfers their ticket to another person from the ticket page.",
        "outcome": "A cancellation becomes a different guest instead of an empty chair.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "gs-event-analytics",
        "title": "See what the event actually did",
        "what": "After an event the host gets an analytics view and a summary covering bookings and who attended.",
        "outcome": "You find out which events are worth repeating, so the second one is better than the first instead of another guess.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "price-a-job-consistently",
        "title": "Pricing a job the same way twice",
        "what": "A project estimator that builds a price from the actual scope, so two people quoting the same job land in the same place.",
        "outcome": "Your pricing is consistent whoever quotes it, and you stop discovering you undercharged after the work is done.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "enquiry-into-the-system-you-already-use",
        "title": "A booking form that files itself into the system you already run on",
        "what": "A multi-step enquiry form that captures the full brief, then posts it straight into the booking system the business already uses, as well as emailing the team.",
        "outcome": "An enquiry arrives already filed where the team works, so nothing is retyped and nothing is lost between the two.",
        "product": "The DFC Experience",
        "flagship": false
      },
      {
        "id": "gs-calendar-sync",
        "title": "The event lands in the calendar you actually check",
        "what": "Events a member books are pushed into their own Google Calendar, so the commitment lives where they plan their week.",
        "outcome": "You turn up, because the thing you committed to is in the calendar you actually run your week from.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "book-you-inside-your-own-app",
        "title": "Booking you without leaving your own app",
        "what": "Customers book time with the business from inside its own app, against real availability, with no third-party booking page.",
        "outcome": "Booking happens inside your own product, so the whole experience is yours and nobody is advertised to on the way.",
        "product": "ProFlow Physiques",
        "flagship": false
      },
      {
        "id": "ie-live-leaderboard",
        "title": "A live leaderboard for the people doing the selling",
        "what": "Per-person figures for work handled, deals closed and commission earned, on one screen that updates as things happen.",
        "outcome": "Everyone can see where they stand without anyone building a spreadsheet on a Friday afternoon.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "ie-money-alerts",
        "title": "Told the moment money lands",
        "what": "Payments and earnings raise a notification to the people they concern, by email and in chat, with the amount and what it was for.",
        "outcome": "Nobody refreshes a dashboard to find out whether the invoice was paid.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "academy-pick-your-coach-pricing",
        "title": "Pick your coach and every price on the page changes",
        "what": "A visitor chooses which coach they want to work with, and every price, package and checkout button on the page re-prices itself to that coach's own rates before they click anything.",
        "outcome": "Coaches who charge different rates can share one page instead of needing one page each, and nobody is ever quoted a figure that belongs to somebody else.",
        "product": "ProFlow Academy Marketing Site",
        "flagship": false
      },
      {
        "id": "academy-unavailable-coach-cannot-be-booked",
        "title": "A coach who is not taking clients yet cannot be booked, even from an old link",
        "what": "Coaches are marked available or not, and an unavailable one is refused everywhere: the picker will not select them, and a saved or shared link naming that coach falls back instead of quietly letting the booking through.",
        "outcome": "The business decides who is taking work, and a link someone kept from last month cannot sell a slot that does not exist.",
        "product": "ProFlow Academy Marketing Site",
        "flagship": false
      },
      {
        "id": "academy-checkout-without-a-basket",
        "title": "Buy in one tap, with no basket and no account first",
        "what": "Each membership, package and single session goes straight to its own secure checkout for the chosen coach, so there is no basket to build and no account to create before paying.",
        "outcome": "Fewer steps between wanting it and paying for it, and nothing to abandon halfway.",
        "product": "ProFlow Academy Marketing Site",
        "flagship": false
      },
      {
        "id": "academy-post-payment-expectation-page",
        "title": "A page after payment that tells them what happens next",
        "what": "Paying lands the customer on a page that explains what happens now and when their login arrives, rather than dropping them back on the homepage. It is deliberately kept out of search, because it is a fulfilment page, not a shopfront.",
        "outcome": "The business gets fewer 'I have paid, what now?' messages, and a new customer's first experience is being told what is happening.",
        "product": "ProFlow Academy Marketing Site",
        "flagship": false
      },
      {
        "id": "physiques-never-invents-a-checkout",
        "title": "A price on screen never links to a different price at checkout",
        "what": "Every plan either has a real checkout behind it or sends the visitor to the enquiry form. Nothing is allowed to link to a payment page that charges a different figure from the one being advertised, and a plan with no checkout yet still appears without a broken button.",
        "outcome": "The business can add or change a plan the same day without risking a customer being charged a number they never agreed to.",
        "product": "ProFlow Physiques Marketing Site",
        "flagship": false
      },
      {
        "id": "mta-service-page-each",
        "title": "A page for each service, written for the client rather than the profession",
        "what": "Bookkeeping, business accounts, VAT, tax returns and fractional finance director each get their own page, explained in the words a worried business owner would use rather than the language of the profession.",
        "outcome": "Somebody searching for the one thing they are anxious about finds a page about that thing, and understands it without looking a term up.",
        "product": "My Trusted Accountant",
        "flagship": false
      },
      {
        "id": "mta-testimonials-that-belong-to-people",
        "title": "Client words carried up front, not buried on a page nobody visits",
        "what": "Real client testimonials run through the site rather than sitting on a separate page, because trust is the thing an accountant is actually selling and it has to be visible where the decision is made.",
        "outcome": "Credibility does the work on the page where someone decides whether to get in touch, instead of on a page they never reach.",
        "product": "My Trusted Accountant",
        "flagship": false
      }
    ]
  },
  {
    "slug": "portals-and-paperwork",
    "name": "Client portals and paperwork",
    "blurb": "A place for a customer to see their own job, and documents that sign themselves.",
    "items": [
      {
        "id": "esign-retainer",
        "title": "Auto contract e-sign on close",
        "what": "When a deal is set up the system creates an e-signature agreement from a template and hands the client a signing link, safely so it is never duplicated.",
        "outcome": "Contracts get signed as part of closing, so nobody chases paperwork or emails PDFs back and forth.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "client-intake-forms",
        "title": "Token link client intake forms",
        "what": "Questionnaire links, templated by service type, that a client opens without logging in to submit their project details, captured as a snapshot tied to the deal.",
        "outcome": "Everything needed to start a job comes in through one link, instead of a long back and forth of emails.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "client-portal",
        "title": "A login where your client can see their own job",
        "what": "A client-facing portal where a customer signs in and sees their own project, documents, invoices and progress, separate from the team's view of it.",
        "outcome": "Clients answer their own questions at eleven at night, and the team stops being a lookup service.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "token-link-documents",
        "title": "Send a document without making anyone create an account",
        "what": "Invoices, contracts, questionnaires and review requests are sent as a secure one-off link. The recipient opens it and acts on it with no sign-up, no password and no app.",
        "outcome": "Far more people finish what you sent them, because there is nothing in the way but the thing itself.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "documents-behind-a-gate",
        "title": "Sharing a document without putting it on the internet",
        "what": "Sensitive documents sit behind an access gate on the site, shared with the people who should have them and nobody else.",
        "outcome": "The people who should see a document can, and it is not sitting on a public URL a competitor can find.",
        "product": "The DFC Experience",
        "flagship": false
      },
      {
        "id": "ie-feedback-link",
        "title": "Feedback from a link, with nobody creating an account",
        "what": "A private link the customer opens and answers in place, no sign-up, no password, filed straight against the job it belongs to.",
        "outcome": "You actually hear what people thought, because you did not ask them to register first.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "pf-design-system",
        "title": "A design system, so every screen matches the last one",
        "what": "One living style reference inside the product holding the type, colour, spacing and components every screen is built from.",
        "outcome": "The app still looks like one product after a year of changes, and a new feature costs less because nobody redesigns the basics.",
        "product": "ProFlow Academy",
        "flagship": false
      }
    ]
  },
  {
    "slug": "sales-and-follow-up",
    "name": "Sales and follow up",
    "blurb": "Finding the work, qualifying it, and chasing it whether anyone remembers or not.",
    "items": [
      {
        "id": "smart-intake-quiz",
        "title": "Smart lead intake quiz",
        "what": "A multi-step questionnaire that asks about a business's industry, tools, headaches, website and goals, works out a recommended approach from the answers, then emails a formatted brief to the team and posts the lead to a webhook.",
        "outcome": "Every enquiry arrives already diagnosed with a suggested direction, so sales conversations start warm and nobody chases a cold form fill.",
        "product": "Ownly Digital",
        "flagship": false
      },
      {
        "id": "automated-lead-finder",
        "title": "Automated lead finder and enricher",
        "what": "A background pipeline that discovers businesses from sources like Companies House, de-duplicates them into one record per company, filters out sectors you do not serve, and enriches each with an online-presence and AI analysis before queuing them for outreach.",
        "outcome": "The owner wakes up to a fresh, cleaned list of qualified prospects instead of trawling directories by hand.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "click-to-call-crm",
        "title": "Every call kept by the business, not the phone it was made on",
        "what": "Reps call from the provider's own app on their own phone. The customer sees the business number, never the rep's mobile, and afterwards the call length, whether it connected, the outcome and the recording are captured against the business automatically.",
        "outcome": "The history of every customer conversation belongs to the business, so when somebody leaves the relationships do not walk out with them.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "commission-self-billing",
        "title": "Automatic commission invoicing",
        "what": "A self-billing system that generates commission invoices for reps as PDFs, emails them, tracks a ledger, and flips entries from earned to paid when an invoice is settled.",
        "outcome": "The sales team gets paid correctly and on time, with no hand-calculated commissions or invoices to write.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "review-collection",
        "title": "Automated review and testimonial collection",
        "what": "Secure expiring links let finished-project clients leave a review or a guided testimonial, with the requests emailed automatically.",
        "outcome": "Social proof builds steadily from happy clients without the owner personally asking each one.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "referral-program",
        "title": "Built-in referral and affiliate program",
        "what": "A referral system giving each client or affiliate a shareable link that earns a percentage of what referred customers spend for as long as they stay, with a portal to track it.",
        "outcome": "Existing clients become a paid sales channel that runs itself.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "project-qualification-quiz",
        "title": "Multi-step project qualification quiz",
        "what": "A stepped enquiry form that asks a prospective client about their industry, current tools, operational headache, team size, website status and twelve-month goals before collecting contact details. With Cloudflare Turnstile bot protection on submission.",
        "outcome": "Every enquiry arrives with a diagnosis already written, so the first sales call is a conversation between peers rather than a fact-finding exercise.",
        "product": "Ownly Digital",
        "flagship": false
      },
      {
        "id": "gs-discover-matching",
        "title": "Matched on what you sell against what they are looking for",
        "what": "Swipe-style discovery where the match score is built from commercial fit, not from job titles or mutual friends. The heaviest single factor by a distance is what you offer matched against what the other person says they are looking for, and a vague overlap deliberately scores almost nothing. It then adds industry overlap, what each side is looking for, business stage, proximity, and a bonus for COMPLEMENTARY industries rather than identical ones. Nobody can message anybody until both have said yes.",
        "outcome": "The people you are put in front of are the ones whose needs match what you actually sell, so a conversation is commercially worth having before it starts.",
        "product": "GrowthSync",
        "flagship": true
      },
      {
        "id": "gs-profile-analytics",
        "title": "See who looked at your profile",
        "what": "Profile views and basic stats are visible to every member. Advanced analytics are an Elite feature.",
        "outcome": "You find out who is already interested but has not said anything, which is a much shorter conversation than starting cold.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "gs-referrals-ambassador",
        "title": "Referrals and the ambassador programme",
        "what": "Every member gets a referral link and earns a reward when someone they refer completes verification. There is also an ambassador programme that tracks referrals and commission.",
        "outcome": "The introductions you already make for free start paying you.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "gs-event-reviews",
        "title": "Reviews after an event",
        "what": "Members are prompted to leave a review after an event, building a visible track record for the host.",
        "outcome": "Your next event fills more easily than your last one, because there is public proof the first was worth attending.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "gs-leaderboard-xp",
        "title": "XP, levels and a leaderboard",
        "what": "Activity in the app earns XP with levels and a public leaderboard, including profile score rankings.",
        "outcome": "You keep going instead of signing up, poking around once and never coming back.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "gs-likes-you",
        "title": "A list of people who already want to talk to you",
        "what": "Sync requests you have received are shown with a count. Free members work through them one at a time; the full list is a paid feature.",
        "outcome": "There is a list of business owners who have already decided they want to talk to you. Most people never look at it.",
        "product": "GrowthSync",
        "flagship": true
      },
      {
        "id": "gs-meetings-reputation",
        "title": "Meetings you can mark complete, feeding a reputation score",
        "what": "Members schedule a meeting and mark it complete, which feeds a reputation score shown on their profile.",
        "outcome": "Other members can see who actually turns up, so being reliable becomes a reason people recommend you.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "gs-honest-match-score",
        "title": "A matcher built to tell you when you are NOT a good fit",
        "what": "Each pairing gets a fit score out of 100 plus written reasons, shown on profile cards, the dashboard and profile pages as labels like Ideal Match at 85 and above. The scoring is deliberately strict, and a shared industry counts for very little because the same industry is as often a competitor as a collaborator. What moves the score is one member offering what the other is looking for, checked in both directions. Same two people always get the same number.",
        "outcome": "You find out a match is weak before you spend an hour on the call, which is the opposite of every network that flatters you into wasting your time.",
        "product": "GrowthSync",
        "flagship": true
      },
      {
        "id": "automatic-chasing",
        "title": "The follow-up that happens whether you remember or not",
        "what": "Outstanding forms and requests are chased automatically on a schedule until they are completed, and the chasing stops the moment they are.",
        "outcome": "Nobody has to be the nag, and nothing stalls for a fortnight waiting on one form.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "which-deals-will-close",
        "title": "Which of these is actually going to happen",
        "what": "Every open deal carries a probability worked out from what has happened on it so far, so the pipeline is ordered by what is real rather than by what was entered most recently.",
        "outcome": "You spend the week on the deals that will land, instead of the ones that shout loudest.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "one-next-action",
        "title": "Every customer shows the one next thing",
        "what": "Each lead and job displays a single next action worked out from its state and history, rather than leaving the team to decide what to do from a list.",
        "outcome": "Anyone on the team can pick it up and know exactly what to do next, including the person who started on Monday.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "same-customer-twice",
        "title": "Catching the same customer entered twice",
        "what": "Duplicate records are detected on the way in and merged, matching on company identity rather than exact text, so one customer is one record.",
        "outcome": "One customer is one record, so nobody gets contacted twice and no history goes missing.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "dormant-customers-resurfaced",
        "title": "The customers who went quiet, brought back up",
        "what": "Contacts and customers who have gone quiet are surfaced automatically for re-engagement, based on how long since anything happened rather than on anyone remembering.",
        "outcome": "Work comes back from people who already know and like you, at no acquisition cost.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "replies-matched-automatically",
        "title": "A reply that finds its way back to the right job",
        "what": "Inbound email replies are matched back to the customer and conversation they belong to automatically, rather than landing in a shared inbox for somebody to sort.",
        "outcome": "A reply lands against the right job on its own, so a real answer never gets buried.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "knows-its-a-bank-holiday",
        "title": "Automation that knows it is a bank holiday",
        "what": "Scheduled chasing and follow-up are aware of UK bank holidays and working days, so nothing automated lands on a day nobody is working.",
        "outcome": "Automated messages arrive when a human would have sent them, so they read as attentive rather than robotic.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "is-this-contact-any-good",
        "title": "Knowing whether you can actually reach someone",
        "what": "Each contact carries a confidence and contactability score based on what is actually known about them, so bad data is visible instead of being discovered on the phone.",
        "outcome": "You spend the morning on the people you can actually reach.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "gs-profile-preview",
        "title": "Seeing yourself the way strangers see you",
        "what": "A preview of a member's own profile exactly as another member sees it, before they decide whether to reach out.",
        "outcome": "You fix the thing that was quietly putting people off, because for the first time you can see it.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "customers-grouped-how-you-work",
        "title": "Grouping customers the way you actually think about them",
        "what": "Customers can be organised into groups and divisions that match how the business really works, and everything else follows those groups.",
        "outcome": "The system organises people the way you already do, so nobody has to translate between the two.",
        "product": "ProFlow Physiques",
        "flagship": false
      },
      {
        "id": "ie-lookalike",
        "title": "Finding more businesses like the customers you already like",
        "what": "Take a customer you want more of and the system scores everyone else against them on industry, niche, location and size, and hands back the closest matches.",
        "outcome": "You aim at the people who look like your best work instead of working alphabetically down a list.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "ie-verify-real",
        "title": "Checking a business is real before anyone chases it",
        "what": "A lookup against the official company record that confirms the business exists, is still trading, and matches the details you were given.",
        "outcome": "Your team spends its time on companies that exist, and you find out about a dissolved one before you invoice it.",
        "product": "An internal business system",
        "flagship": false
      }
    ]
  },
  {
    "slug": "apps-and-members",
    "name": "Apps and members",
    "blurb": "Software people carry in a pocket, built for phones with one bar of signal.",
    "items": [
      {
        "id": "drip-video-academy",
        "title": "Member video academy with timed unlocks",
        "what": "A private members video library where each lesson unlocks a set number of days after the member starts, and the playable video is only released server-side once it is actually unlocked for that person.",
        "outcome": "You can sell a structured course or coaching program that releases content on schedule, keeping members engaged for months instead of bingeing and leaving.",
        "product": "ProFlow Academy",
        "flagship": false
      },
      {
        "id": "custom-checkin-builder",
        "title": "Custom check-in form builder",
        "what": "A builder where a coach assembles check-in forms from question types (text, number, scale, yes or no, choice, photo, video) and clients fill them in, uploading progress photos and videos to a private store.",
        "outcome": "You design exactly the check-in your service needs once, and clients report back in a consistent format that lands straight in your inbox.",
        "product": "ProFlow Academy",
        "flagship": false
      },
      {
        "id": "progress-photo-compare",
        "title": "Progress photo and video tracking",
        "what": "A progress screen where clients capture or upload photos and videos over time, with native camera capture, on-device video compression, and a side-by-side comparison of any two dates.",
        "outcome": "Clients literally see their results stacking up, which keeps them motivated, proves your service works, and gives you before-and-after proof for marketing.",
        "product": "ProFlow Academy",
        "flagship": false
      },
      {
        "id": "transformation-carousel",
        "title": "Swipeable client transformation showcase",
        "what": "An auto-advancing photo carousel showing client transformation results, with swipe gestures on mobile, left and right arrow buttons, keyboard navigation, a progress bar, and automatic pausing when the user navigates manually or has reduced motion enabled.",
        "outcome": "Prospective clients see a steady stream of real results the moment they land, building the social proof that coaching programmes are sold on.",
        "product": "Elevate Physique Coaching",
        "flagship": false
      },
      {
        "id": "voice-note-on-the-thing-itself",
        "title": "A spoken note attached to the exact thing it is about",
        "what": "A short voice note recorded and attached to a specific item, so the explanation sits on the thing rather than in a separate message.",
        "outcome": "You say it once, in your own voice, and it is there every time somebody looks at that item.",
        "product": "ProFlow Physiques",
        "flagship": false
      },
      {
        "id": "message-a-slice-of-your-customers",
        "title": "Messaging one group without messaging everybody",
        "what": "Broadcasts sent to a defined segment of customers rather than the whole list, delivered as a push notification to the phone.",
        "outcome": "People read your messages, because the only ones they get are the ones meant for them.",
        "product": "ProFlow Physiques",
        "flagship": false
      },
      {
        "id": "video-that-uploads-from-a-phone",
        "title": "Video that actually uploads from a phone in a car park",
        "what": "Video is captured through the phone's own camera and compressed on the device before upload, so a large clip goes up over a normal mobile connection.",
        "outcome": "People actually send the video, because it uploads in seconds instead of failing on mobile data.",
        "product": "ProFlow Physiques",
        "flagship": false
      },
      {
        "id": "captions-on-your-own-videos",
        "title": "Captions on your videos, without paying per minute",
        "what": "Uploaded videos carry captions, so they can be watched with the sound off and by people who need them.",
        "outcome": "Your videos work on a train, in an office and for anyone who needs captions, which is most of your audience.",
        "product": "ProFlow Physiques",
        "flagship": false
      },
      {
        "id": "build-once-reuse-forever",
        "title": "Templates so the same plan is not rebuilt every time",
        "what": "Reusable templates for the things a business gives every customer, assembled once and applied rather than rebuilt each time.",
        "outcome": "You build it once properly, then apply and adjust, so every customer gets your best version rather than your Tuesday version.",
        "product": "ProFlow Physiques",
        "flagship": false
      },
      {
        "id": "everything-you-know-in-one-library",
        "title": "Everything you know, in one place customers can search",
        "what": "A searchable library of the business's own videos and material, organised by category, available to customers inside the product.",
        "outcome": "Customers answer their own questions from your material, and your expertise stops being something only you can deliver live.",
        "product": "ProFlow Academy",
        "flagship": false
      },
      {
        "id": "notification-that-lands-on-the-phone",
        "title": "A message that reaches the phone, not the inbox",
        "what": "Push notifications to the customer's phone for the things that matter, rather than another email into a full inbox.",
        "outcome": "The thing that matters reaches them on the device they actually look at.",
        "product": "ProFlow Academy",
        "flagship": false
      },
      {
        "id": "physiques-proof-without-invented-numbers",
        "title": "Real client photographs, and no invented numbers",
        "what": "The results page is built from real client before-and-after photographs, with no names, no weights and no timeframes attached, because the business cannot stand behind figures it did not measure.",
        "outcome": "The proof is the part a stranger actually believes, and nothing on the page can be challenged as a claim the business cannot back up.",
        "product": "ProFlow Physiques Marketing Site",
        "flagship": false
      },
      {
        "id": "physiques-real-photos-not-stock",
        "title": "Every photograph is a real client, and the page says so",
        "what": "The site states plainly that every photograph on it is a real client or the coach himself, with no stock photography and no paid models, and the galleries are built to hold phone photographs taken backstage and in car parks rather than cropping them into a tidy grid.",
        "outcome": "In an industry full of borrowed images, being visibly the real thing is the differentiator, and the layout stops real photographs looking like a mistake.",
        "product": "ProFlow Physiques Marketing Site",
        "flagship": false
      }
    ]
  },
  {
    "slug": "sites-and-content",
    "name": "Sites and content",
    "blurb": "Pages a client can change themselves, in every language they sell in.",
    "items": [
      {
        "id": "one-tap-translation",
        "title": "One-tap AI translation",
        "what": "A translate control on community posts, replies and messages that translates the text on demand with AI, caches it, and lets the reader flip back to the original.",
        "outcome": "Members who speak different languages all take part in your community and message your team, so you can serve an international audience without hiring translators.",
        "product": "ProFlow Academy",
        "flagship": false
      },
      {
        "id": "self-serve-cms",
        "title": "Self-serve content studio",
        "what": "An embedded editing studio letting the owner update copy, images, packages, artists and venues themselves.",
        "outcome": "The owner keeps the site current on their own schedule, with no developer needed for everyday changes.",
        "product": "The DFC Experience",
        "flagship": false
      },
      {
        "id": "artist-video-profiles",
        "title": "Artist profile pages with video and social media",
        "what": "Individual pages per performer, each with a Vimeo video gallery (hover-to-play previews on desktop, click on mobile), an embedded Spotify playlist, curated Instagram posts pulled from the account, and a gallery of event photos. All managed through a CMS so the owner can add, edit and reorder without a developer.",
        "outcome": "Prospective clients explore each performer in detail on the site, see real video and real reviews, and enquire with confidence. Instead of asking for a showreel by email.",
        "product": "The DFC Experience",
        "flagship": false
      },
      {
        "id": "whatsapp-instant-contact",
        "title": "WhatsApp floating contact button",
        "what": "A persistent WhatsApp button fixed to the corner of every page, with a pre-filled opening message, a pulsing indicator, and a tooltip showing hours. So a visitor can start a conversation in one tap without filling in a form.",
        "outcome": "Visitors who are ready to talk can reach the team instantly on the channel they already use every day, instead of waiting for a contact form response.",
        "product": "The DFC Experience",
        "flagship": false
      },
      {
        "id": "coaching-apply-form",
        "title": "Coaching application and qualification form",
        "what": "A multi-field application form that collects service preference, goals, training experience and availability before opening the applicant's email app with a pre-filled draft to the coach. So the first conversation is already context-rich when it arrives.",
        "outcome": "The coach only has conversations with people who have already committed enough to write down their goals and hit send. No wasted calls with people who were never a fit.",
        "product": "Elevate Physique Coaching",
        "flagship": false
      },
      {
        "id": "a-page-for-every-place-you-work",
        "title": "A page for every area you actually cover",
        "what": "Generated landing pages for each area a business serves, each with its own venues, content and structured data, rather than one page listing everywhere.",
        "outcome": "You show up for the places you actually work, and the visitor lands on a page about their town rather than a list of thirty.",
        "product": "The DFC Experience",
        "flagship": false
      },
      {
        "id": "site-that-refreshes-itself",
        "title": "A website that looks after itself overnight",
        "what": "Media and covers are pulled in automatically when content is added, and a nightly job backfills anything that failed and clears out empty entries, so the site self-heals.",
        "outcome": "A bad night fixes itself by morning, instead of leaving a broken tile on a page nobody checks.",
        "product": "The DFC Experience",
        "flagship": false
      },
      {
        "id": "see-it-before-it-goes-live",
        "title": "Seeing a change before the public does",
        "what": "Draft mode lets the owner view unpublished changes on the real site, exactly as a visitor would, before anything is published.",
        "outcome": "You see a change on the real site first and publish it when you are happy, not before.",
        "product": "The DFC Experience",
        "flagship": false
      },
      {
        "id": "front-page-that-changes-itself",
        "title": "A homepage that is not the same every week",
        "what": "Selected content is rotated on a weekly schedule, so a returning visitor sees something different without anyone editing anything.",
        "outcome": "Returning visitors see something new, and nobody has to remember to change it.",
        "product": "The DFC Experience",
        "flagship": false
      },
      {
        "id": "gs-multi-language",
        "title": "A network that is not only in English",
        "what": "The platform runs in multiple languages, so members who do not work in English can take part properly rather than half following along.",
        "outcome": "You do business with people you would previously have written off as too much friction.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "gs-friendly-errors",
        "title": "Errors written for a customer, not a developer",
        "what": "Every failure is translated into a plain sentence naming what went wrong and what to do about it, with the technical detail sent to us instead of shown to the person.",
        "outcome": "A customer who hits a problem knows what to do next, rather than reading a database message and deciding your software is broken.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "gs-search-landing-pages",
        "title": "A page for every thing people actually search for",
        "what": "Dedicated landing pages built per search intent rather than one catch-all homepage, each with its own copy, title and structured data.",
        "outcome": "You show up for the specific thing somebody typed, and they land on a page about that thing instead of a homepage they have to decode.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "dfc-content-before-cms",
        "title": "A site already full of real content before anyone logs into the CMS",
        "what": "Every page ships with real, structured content built in, and the content system takes over the moment it is connected without the pages changing shape.",
        "outcome": "The site is live and selling on day one instead of waiting on somebody to sit down and fill it in.",
        "product": "The DFC Experience",
        "flagship": false
      },
      {
        "id": "owner-publishes-site-updates-itself",
        "title": "He publishes in the app, the website updates itself",
        "what": "Articles written and published inside the business's own app appear on the public marketing site on their own, through periodic rebuilds, with no deploy and no developer.",
        "outcome": "The owner controls what the website says without waiting on anyone, so the site stays current instead of frozen at whatever it said at launch.",
        "product": "ProFlow Physiques Marketing Site",
        "flagship": false
      },
      {
        "id": "academy-nine-languages-no-half-pages",
        "title": "Nine languages, and no page that gives up halfway through",
        "what": "The whole site is published in nine languages with English as the single source of truth, and every other language is held level with it, so a visitor never lands on a page that starts in their language and reverts to English partway down.",
        "outcome": "The business can sell into countries it has no staff in, and a visitor reading in their own language is never made to feel like an afterthought.",
        "product": "ProFlow Academy Marketing Site",
        "flagship": false
      }
    ]
  },
  {
    "slug": "running-the-business",
    "name": "Running the business",
    "blurb": "The screens, alerts and safeguards that tell you what is happening while you are busy.",
    "items": [
      {
        "id": "gs-communities",
        "title": "Asking your own industry, and getting a real answer for nothing",
        "what": "Industry and interest communities where any member with a profile can post, comment and react, free tier included, so a business owner can put a real question to people who actually do the same thing. Elite-only communities exist alongside the open ones.",
        "outcome": "You get a straight answer from somebody who has already solved it, for nothing, without announcing your gap to your own market.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "gs-opportunities",
        "title": "Post an opportunity and pick who takes it",
        "what": "Members post a specific opportunity, a partnership, a role, a collaboration or work they need doing, others apply, and the poster accepts or declines.",
        "outcome": "Work and partnerships that never get advertised reach you, which is where most real business actually comes from.",
        "product": "GrowthSync",
        "flagship": true
      },
      {
        "id": "gs-services",
        "title": "List what you actually sell",
        "what": "A member lists the services their business provides, takes enquiries against them, and accepts or declines each one.",
        "outcome": "People approach you already knowing what you sell, so the conversation starts at the job instead of at small talk.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "live-team-board",
        "title": "What the team is doing, live",
        "what": "A live board showing activity as it happens across the team, with a feed of what changed, who did it and when.",
        "outcome": "You can see a bad week on the Tuesday, while there is still time to change it.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "a-days-work-decided-for-you",
        "title": "A screen that tells you what today is",
        "what": "A daily view that assembles the day's work from everything due, overdue, promised or waiting, so the day starts already prioritised.",
        "outcome": "The day starts already decided, so the first hour goes on work instead of triage.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "jobs-that-say-when-they-die",
        "title": "Automations that tell you when they stop working",
        "what": "Every scheduled job records a heartbeat, and a job that stops running raises an alert instead of failing quietly.",
        "outcome": "A broken automation announces itself the same day instead of being discovered by a customer.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "something-looks-wrong-digest",
        "title": "A daily note when something looks off",
        "what": "A digest that watches for things that look wrong (a metric that moves oddly, a process that stops producing) and reports them, staying silent when everything is normal.",
        "outcome": "You are told when something looks wrong, rather than being expected to go and look.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "new-starter-checklist",
        "title": "Onboarding a new person without doing it from memory",
        "what": "A structured onboarding checklist per new starter, tracking paperwork, access and training completion, with the gaps visible rather than assumed.",
        "outcome": "Every new person gets the same first week, and you can see at a glance what is still missing.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "training-that-stays-put",
        "title": "Training that does not leave when someone does",
        "what": "Training videos and materials held in the system per role, watched and tracked, so how you do things lives somewhere other than in one person's head.",
        "outcome": "The way you work survives a resignation, and new people learn it without booking anyone's time.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "job-finishes-itself",
        "title": "What happens when a job is marked done",
        "what": "Completing a project fires the follow-on work automatically: the review request, the case study request, the final invoice and the internal tidy-up.",
        "outcome": "Every finished job produces the proof and the paperwork it should, without anybody remembering to.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "hiring-without-a-spreadsheet",
        "title": "Applications that arrive somewhere useful",
        "what": "A shareable application link that collects applicants into a tracked list with their answers attached, rather than into an inbox.",
        "outcome": "Applicants arrive already organised and comparable, so hiring takes evenings rather than weeks.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "unsubscribes-that-actually-work",
        "title": "Marketing that respects the law without you thinking about it",
        "what": "Unsubscribe links, per-contact email preferences and suppression are enforced at the sending layer, so an opt-out is honoured everywhere automatically.",
        "outcome": "An opt-out is honoured everywhere the first time, so you never get the angry reply or the complaint.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "forms-that-bots-cannot-fill",
        "title": "Keeping the rubbish out of your enquiry form",
        "what": "Public forms are protected with invisible bot checks, a hidden honeypot field and per-address rate limiting, so junk submissions never reach the team.",
        "outcome": "Every enquiry that reaches a human is a real person, so none get skimmed past.",
        "product": "The DFC Experience",
        "flagship": false
      },
      {
        "id": "gs-weekly-summary",
        "title": "A weekly note on what your network actually did",
        "what": "A weekly summary of a member's activity and what happened around them: who looked, who connected, what came up.",
        "outcome": "You can see whether the effort is working, so you keep doing the part that is and drop the part that is not.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "gs-safety-controls",
        "title": "Being able to shut a conversation down",
        "what": "Members can block and report other members, with moderation behind it, so a bad interaction ends at the block.",
        "outcome": "People stay open and reachable, because they know they can end a conversation whenever they want.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "gs-invite-your-people",
        "title": "Bringing the people you already trust in with you",
        "what": "Members can invite people they already know into the platform, so their existing relationships come with them rather than being left behind.",
        "outcome": "You start with the people who already know you, and everything you build sits on top of that.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "gs-take-your-data",
        "title": "Your contacts are yours to take with you",
        "what": "Members can export their own data out of the platform whenever they want.",
        "outcome": "You build on something you can walk away from, which is the only kind worth building on.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "numbers-that-cannot-disagree",
        "title": "Numbers that cannot contradict each other",
        "what": "Totals are calculated from their parts in one place and never stored separately, so two figures on the same screen can never disagree.",
        "outcome": "Every number in the system comes from one calculation, so nobody has to work out which screen to believe.",
        "product": "ProFlow Physiques",
        "flagship": false
      },
      {
        "id": "who-has-not-replied-yet",
        "title": "Seeing who has not come back to you",
        "what": "Outstanding responses are tracked and counted, so who is overdue is visible at a glance rather than worked out from memory.",
        "outcome": "The person quietly drifting away is visible while there is still time to do something about it.",
        "product": "ProFlow Academy",
        "flagship": false
      },
      {
        "id": "report-something-in-your-own-community",
        "title": "A community people can report something in",
        "what": "Members can report content, and reports arrive in a moderation queue for the business to act on.",
        "outcome": "You can run a space for your customers without lying awake about what gets posted in it.",
        "product": "ProFlow Physiques",
        "flagship": false
      },
      {
        "id": "gs-get-app-redirect",
        "title": "One link that sends everyone to the right app store",
        "what": "A single share link that detects the device and sends iPhone users to the App Store, Android users to Google Play, and everyone on a laptop to a page that works anyway.",
        "outcome": "You put one link in a bio, an email or a poster instead of three, and nobody lands on a store page for a phone they do not own.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "gs-product-analytics",
        "title": "Knowing which features people actually use",
        "what": "Product analytics wired through the app so every screen and action is measured, with the events named after what the business cares about rather than what the code does.",
        "outcome": "You stop guessing which parts earn their keep, and the next thing you pay to build is the thing the numbers point at.",
        "product": "GrowthSync",
        "flagship": false
      },
      {
        "id": "ie-case-study-request",
        "title": "Asking a happy customer for a case study, without writing the email",
        "what": "A one-click request that emails the customer a private link, valid for a month, where they answer a few questions and the answers come back attached to their job.",
        "outcome": "You collect proof while the job is still fresh, instead of remembering six months later that you never asked.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "ie-audit-trail",
        "title": "A record of who changed what, and when",
        "what": "Sensitive actions write an audit entry naming the person, the target and the change, kept separately from the thing that changed.",
        "outcome": "When something looks wrong you can answer who did it in a minute, rather than reconstructing it from memory.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "ie-safe-csv",
        "title": "Everything out to a spreadsheet, without handing over a weapon",
        "what": "Any list exports to CSV through one shared writer that escapes every cell, including the formula characters that would otherwise run as code when the file is opened.",
        "outcome": "Your data is yours to take out, and the file you send a client cannot execute something on their machine.",
        "product": "An internal business system",
        "flagship": false
      },
      {
        "id": "pf-haptics",
        "title": "An app that feels native, down to the buzz",
        "what": "Real haptic feedback on the actions that matter, loaded only on a phone so the web version never carries the weight.",
        "outcome": "It feels like an app somebody built for the phone, not a website in a wrapper, which is most of what people mean by quality.",
        "product": "ProFlow Physiques",
        "flagship": false
      },
      {
        "id": "pf-client-editable-stats",
        "title": "Headline numbers the client edits themselves",
        "what": "The figures on the front of the app are read from a record the owner can edit in their own admin screen, not typed into the code.",
        "outcome": "When the number changes you change it yourself in ten seconds, instead of paying somebody to deploy.",
        "product": "ProFlow Academy",
        "flagship": false
      },
      {
        "id": "pf-fresh-on-return",
        "title": "Fresh data the moment you come back to it",
        "what": "Bringing the app back into focus quietly refetches what is on screen, so nothing shown is left over from yesterday.",
        "outcome": "Nobody makes a decision from a stale number because they left the app open overnight.",
        "product": "ProFlow Academy",
        "flagship": false
      },
      {
        "id": "pf-onboarding-message",
        "title": "A welcome message that is already written",
        "what": "When a customer is invited, the system builds the ready-to-send message including their link and the right store for their phone, for staff to copy and send.",
        "outcome": "Every customer gets the same clear welcome, instead of whatever the person on shift could remember to type.",
        "product": "ProFlow Academy",
        "flagship": false
      },
      {
        "id": "answer-first-lead-tool",
        "title": "A free tool that answers first and asks second",
        "what": "A calculator that works the answer out in the browser and shows it the moment the inputs are valid. The email box appears after the result, offering something extra, and never holds the answer back.",
        "outcome": "The tool gets shared, linked to and dropped into group chats instead of bounced off, so it keeps bringing people in long after it was built. The addresses it does collect come from people who already got value.",
        "product": "Elevate Physique Coaching",
        "flagship": false
      },
      {
        "id": "abandoned-enquiry-digest",
        "title": "The enquiries that never finished",
        "what": "A daily digest of people who started the enquiry form, typed a name and email, then drifted off without submitting. The instant alert covers the ones who finish; this covers the ones who do not.",
        "outcome": "Contactable leads that did not exist at all before, surfaced every morning instead of lost.",
        "product": "Elevate Physique Coaching",
        "flagship": false
      },
      {
        "id": "a-page-for-every-service-searched",
        "title": "A page for each thing people actually search",
        "what": "A separate landing page per service, each with its own title, description and canonical, rather than one services page trying to rank for everything.",
        "outcome": "The business turns up for the specific thing someone typed, and the page they land on is about that one thing.",
        "product": "Elevate Physique Coaching",
        "flagship": false
      },
      {
        "id": "academy-in-person-or-online-in-one-choice",
        "title": "Online or in the room, chosen on the same page",
        "what": "Sessions can be bought online or in person, with the actual locations the coaches travel to offered as part of the same choice rather than hidden in an email exchange.",
        "outcome": "Someone who would only ever book in person can see that it is possible without asking, and someone who wants it online is not sent to the wrong page.",
        "product": "ProFlow Academy Marketing Site",
        "flagship": false
      },
      {
        "id": "physiques-eight-programme-pages",
        "title": "A page for each programme, so search can find each one separately",
        "what": "Each coaching programme gets its own page with its own title, cover and explanation, rather than all of them sharing one services page.",
        "outcome": "Someone searching for the specific thing they want lands on a page about exactly that, instead of a general page they have to read through.",
        "product": "ProFlow Physiques Marketing Site",
        "flagship": false
      },
      {
        "id": "physiques-enquiries-reach-the-coach",
        "title": "Enquiries go to the person who answers them",
        "what": "The enquiry form routes straight to the coach rather than into a shared inbox, and is protected against automated submissions without making a real person solve a puzzle.",
        "outcome": "A solo coach sees every genuine enquiry the moment it arrives, and does not lose them among automated rubbish.",
        "product": "ProFlow Physiques Marketing Site",
        "flagship": false
      },
      {
        "id": "mta-analytics-only-after-consent",
        "title": "Nothing is measured until the visitor agrees to it",
        "what": "No analytics or tracking runs until the visitor has actually accepted it, and refusing works properly rather than being remembered as a yes.",
        "outcome": "A practice that advises on compliance is visibly compliant itself, and a visitor who says no is genuinely not tracked.",
        "product": "My Trusted Accountant",
        "flagship": false
      },
      {
        "id": "mta-one-next-step-per-page",
        "title": "Every page ends in the same single next step",
        "what": "Each page finishes with the same one action, so a visitor never has to hunt for how to get in touch, and there are no competing calls to action pulling in different directions.",
        "outcome": "An enquiry does not depend on somebody scrolling back to the top to look for a contact link.",
        "product": "My Trusted Accountant",
        "flagship": false
      }
    ]
  }
] as const;

/** Total number of shipped capabilities, used in the proof counts. */
export const BUILT_COUNT = BUILT_GROUPS.reduce((n, g) => n + g.items.length, 0);

/** Every product represented, in the order they first appear. */
export const BUILT_PRODUCTS: readonly string[] = Array.from(
  new Set(BUILT_GROUPS.flatMap((g) => g.items.map((i) => i.product))),
);
